import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import H from "history";
import Table, { TableColumn } from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import { IRootState } from '../../../store';
import { TableRowProps } from '../../TableRow';
import { cellForColumn } from './SubmissionAssignmentAnalyticsUtils';
import { SubjectSubmissionSummary } from '../../../models/analytics/AssignmentSubmissionSummaries';
import AcademicYearPicker from '../../AcademicYearPicker';
import { openFileDownload } from '../../../utils/Utils';
import SchoolMenu from '../../SchoolMenu';
import { CommandBarButton } from 'office-ui-fabric-react/lib-commonjs';
import SubjectsSubmissionResponse from "../../../models/analytics/SubjectsSubmissionResponse";
import PercentageOverviewDoughnut from "../../assignment-analytics/PercentageOverviewDoughnut";
import SimpleDoughnut from "../../assignment-analytics/SimpleDoughnut";
import {AnalyticsDoughnutPalette} from "../../assignment-analytics/AnalyticsDoughnutUtils";
import SchoolPageTitle from '../../SchoolPageTitle';
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";

interface SubjectsSubmissionAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
}

interface SubjectsSubmissionAnalyticsPageState {
    result?: SubjectsSubmissionResponse;
}

class SubjectsSubmissionAnalyticsPage extends Component<SubjectsSubmissionAnalyticsPageProps & DispatchProps, SubjectsSubmissionAnalyticsPageState> {
    static displayName = SubjectsSubmissionAnalyticsPage.name;

    constructor(props: SubjectsSubmissionAnalyticsPageProps & DispatchProps) {
        super(props);
        this.state = {
            result: undefined
        }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear) {
            await this.getResults();
        }
    }

    async componentDidUpdate(prevProps: Readonly<SubjectsSubmissionAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear) {
            await this.getResults();
        }
    }

    private async getResults(): Promise<void> {
        this.setState({
            result: undefined
        });

        const result = await this.props.apiService.getSubjectsSubmissionsAnalyticsForSchool(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!);

        this.setState({
            result
        });
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private async export(): Promise<void> {
        var result = await this.props.apiService.getSubjectsSubmissionsAnalyticsForSchoolExport(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!)
        openFileDownload(result);
    }

    private static readonly columns: TableColumn[] = [
        { title: "Subject Name", propertyPath: "subjectName", extraDetail: "Subject code", enableSort: true, flex: 4 },
        { title: "Classes with assignments", propertyPath: "classesWithAssignmentsPercentage", extraDetail: "", enableSort: true, flex: 4 },
        { title: "Assignments Received", propertyPath: "submissionsTotal", extraDetail: "Total assignments the students in this subject have received", enableSort: true, flex: 2 },
        { title: "Not Completed", propertyPath: "submissionsNotCompletedPercentage", extraDetail: "Not submitted within 2 weeks of due date", enableSort: true, flex: 2 },
        { title: "Late", propertyPath: "submissionsSubmittedAfterDueDatePercentage", extraDetail: "Submitted within 2 weeks after the due date", enableSort: true, flex: 2 },
        { title: "On Time", propertyPath: "submissionsSubmittedBeforeDueDatePercentage", extraDetail: "Submitted before the due date", enableSort: true, flex: 2 },
    ];

    private readonly rowProps = (item: SubjectSubmissionSummary): TableRowProps => {
        return {
            enableRowLink: true,
            to: `/manage/${this.props.tenantId}/${this.props.schoolCode}/subject-assignment-analytics/${item.subjectCode}?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`
        };
    }

    private buildCharts = (): JSX.Element | undefined => {
        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{marginTop: "0.25rem"}}>
                <div className={"number-stat"}>
                    <div className="number-stat__number number-stat__number--small">
                        {this.state.result!.haveSetAssignmentsCount}
                    </div>
                    <div className={"number-stat__description"}>Subjects have set assignments</div>
                </div>

                <SimpleDoughnut
                    title={"Subjects that have set assignments"}
                    aValue={this.state.result!.haveSetAssignmentsPercentage} aLabel={"Have set assignments"} aColour={AnalyticsDoughnutPalette.green}
                    bValue={this.state.result!.haveNotSetAssignmentsPercentage} bLabel={"Have not set any assignments"} bColour={AnalyticsDoughnutPalette.grey}/>
                <PercentageOverviewDoughnut percentageOverview={this.state.result!.classesWithAssignmentsOverview} title={"Subjects with assignments for all their classes"} labels={PercentageOverviewDoughnut.LABELS_ALL}/>
            </div>
        );
    }

    private buildResults(): JSX.Element {
        if (this.state.result === undefined) {
            return <LoadingCard />;
        }
        else {
            return (
                <Fragment>

                    {this.buildCharts()}
                    
                    <Table
                        title={"Subjects"}
                        items={this.state.result.items}
                        columns={SubjectsSubmissionAnalyticsPage.columns}
                        cellForColumn={cellForColumn}
                        rowPropsForItem={this.rowProps}
                        defaultSortColumnPath={'subjectName'}
                        enableSort={true}>
    
                        <CommandBarButton
                            iconProps={{ iconName: "DownloadDocument" }}
                            text="Export All"
                            onClick={() => this.export()} />
                    </Table>
                </Fragment>
            );
        }
    }

    render() {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Assignment Analytics - Subjects" reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix="Subjects" />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} current={"subject"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SubjectsSubmissionAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(SubjectsSubmissionAnalyticsPage)
